const SUBMIT_DEBOUNCE_MS = 750

type DoNotSellTraits = {
	first_name: string
	last_name: string
	country: string
	state: string
}

type DoNotSellEvent = {
	eventName: 'Form Submitted'
	form_name: 'Do Not Sell My Info'
	form_url: 'https://pvolve.com/pages/do-not-sell-my-info'
	__consentCategories: {
		functional: boolean
		performance: boolean
		marketing: boolean
		dataSharing: false
	}
}

type ShopifyVisitorConsent = {
	preferences?: boolean | string
	analytics?: boolean | string
	marketing?: boolean | string
	sale_of_data?: boolean | string
}

function consentAllowed(value: boolean | string | undefined, fallback = false) {
	if (typeof value === 'boolean') return value
	if (typeof value !== 'string' || value === '') return fallback
	return ['yes', 'true', '1', 'allowed'].includes(value.toLowerCase())
}

function persistShopifySaleOfDataOptOut() {
	return new Promise<ShopifyVisitorConsent>((resolve, reject) => {
		const shopify = window.Shopify
		if (!shopify?.loadFeatures) {
			reject(new Error('Shopify consent API loader is unavailable'))
			return
		}

		shopify.loadFeatures(
			[{ name: 'consent-tracking-api', version: '0.1' }],
			(error) => {
				if (error || !shopify.customerPrivacy?.setTrackingConsent) {
					reject(error || new Error('Shopify consent API is unavailable'))
					return
				}

				const current = shopify.customerPrivacy.currentVisitorConsent?.() || {}
				const categories = window.__pvolveConsent?.categories
				const consent = {
					preferences: consentAllowed(current.preferences, !!categories?.functional),
					analytics: consentAllowed(current.analytics, !!categories?.performance),
					marketing: consentAllowed(current.marketing, !!categories?.marketing),
					sale_of_data: false,
				}
				shopify.customerPrivacy.setTrackingConsent(consent, (result) => {
					if (result && typeof result === 'object' && 'error' in result && result.error) {
						reject(new Error(String(result.error)))
						return
					}
					window.__pvolveConsent = window.__pvolveConsent || {}
					window.__pvolveConsent.categories = window.__pvolveConsent.categories || {}
					window.__pvolveConsent.categories.dataSharing = false
					if (window.pvolveConsent && typeof window.pvolveConsent.persistConsent === 'function') {
						window.pvolveConsent.persistConsent()
					}
					resolve(consent)
				})
			}
		)
	})
}

function initDoNotSellForm(form: HTMLFormElement) {
	const submitButton = form.querySelector<HTMLButtonElement>('button[type="submit"]')
	const submitLabel = form.querySelector<HTMLElement>('[data-submit-label]')
	const submittingLabel = form.querySelector<HTMLElement>('[data-submitting-label]')
	const status = form.querySelector<HTMLElement>('[data-form-status]')
	const emailInput = form.querySelector<HTMLInputElement>('input[name="email"]')
	const countrySelect = form.querySelector<HTMLSelectElement>('[data-country-select]')
	const regionSelect = form.querySelector<HTMLSelectElement>('[data-region-select]')
	const regionContainer = form.querySelector<HTMLElement>('[data-region-container]')
	const successPanel = form.parentElement?.querySelector<HTMLElement>('[data-form-success]')
	const successHeading = successPanel?.querySelector<HTMLElement>('[data-success-heading]')
	const pageHeading = form.parentElement?.querySelector<HTMLElement>('[data-page-heading]')
	const pageDescription = form.parentElement?.querySelector<HTMLElement>('[data-page-description]')
	let submitTimer: number | undefined
	let requestInFlight = false

	if (!submitButton || !status || !emailInput || !countrySelect || !regionSelect || !regionContainer || !successPanel) return
	const submitButtonElement = submitButton
	const statusElement = status
	const countrySelectElement = countrySelect
	const regionSelectElement = regionSelect
	const regionContainerElement = regionContainer
	const successPanelElement = successPanel
	form.noValidate = true

	type ValidatedField = HTMLInputElement | HTMLSelectElement
	const validatedFields = Array.from(form.querySelectorAll<ValidatedField>('input[name="first_name"], input[name="last_name"], input[name="email"], select[name="country"], select[name="state"]'))
	const completeEmailPattern = /^[^\s@]+@[^\s@]+\.[^\s@]+$/

	function setFieldError(field: ValidatedField, message = '') {
		const fieldContainer = field.closest<HTMLElement>('.do-not-sell__field')
		const error = fieldContainer?.querySelector<HTMLElement>('[data-field-error]')
		if (message) field.setAttribute('aria-invalid', 'true')
		else field.removeAttribute('aria-invalid')
		fieldContainer?.classList.toggle('do-not-sell__field--invalid', Boolean(message))
		if (error) {
			if (message) error.textContent = message
			error.hidden = !message
		}
	}

	function validateField(field: ValidatedField) {
		if (field.disabled) {
			setFieldError(field)
			return true
		}

		const value = field.value.trim()
		let message = ''
		if (field.required && !value) {
			message = field.name === 'first_name' ? 'Please enter your first name.' :
				field.name === 'last_name' ? 'Please enter your last name.' :
				field.name === 'country' ? 'Please select your country or region.' :
				field.name === 'state' ? 'Please select your state, province, or region.' :
				'Please complete this field.'
		} else if (field.name === 'email' && !completeEmailPattern.test(value)) {
			message = 'Please enter a valid email address, such as name@example.com.'
		}
		setFieldError(field, message)
		return !message
	}

	function validateForm() {
		const fields = validatedFields.filter((field) => !field.disabled)
		const results = fields.map((field) => ({ field, valid: validateField(field) }))
		const firstInvalid = results.find(({ valid }) => !valid)?.field
		if (firstInvalid) {
			setStatus('Please correct the highlighted fields and try again.', true)
			firstInvalid.focus()
			return false
		}
		statusElement.hidden = true
		return true
	}

	function syncRegions() {
		const selectedCountry = countrySelectElement.options[countrySelectElement.selectedIndex]
		let regions: [string, string][] = []
		try { regions = JSON.parse(selectedCountry?.dataset.provinces || '[]') } catch (_) { regions = [] }
		const hasRegions = regions.length > 0
		const options = regions.map(([value, label]) => new Option(label, value))
		if (hasRegions) options.unshift(new Option('Select a state/province/region', '', true, true))
		regionSelectElement.replaceChildren(...options)
		regionContainerElement.hidden = !hasRegions
		regionSelectElement.disabled = !hasRegions
		regionSelectElement.required = hasRegions
		setFieldError(regionSelectElement)
	}

	validatedFields.forEach((field) => {
		const eventName = field instanceof HTMLSelectElement ? 'change' : 'input'
		field.addEventListener(eventName, () => {
			if (field.hasAttribute('aria-invalid')) validateField(field)
		})
	})
	Array.from(countrySelectElement.options).forEach((option) => {
		const value = option.value.trim()
		const label = option.textContent?.trim()
		if (value === '---' || label === '---') option.remove()
	})
	countrySelectElement.addEventListener('change', syncRegions)
	form.addEventListener('reset', () => window.setTimeout(syncRegions, 0))
	syncRegions()

	function setSubmitting(isSubmitting: boolean) {
		requestInFlight = isSubmitting
		if (isSubmitting) {
			submitButtonElement.disabled = true
			submitButtonElement.setAttribute('aria-disabled', 'true')
		} else {
			submitButtonElement.disabled = false
			submitButtonElement.removeAttribute('disabled')
			submitButtonElement.removeAttribute('aria-disabled')
			submitButtonElement.classList.add('button--primary')
		}
		submitLabel?.toggleAttribute('hidden', isSubmitting)
		submittingLabel?.toggleAttribute('hidden', !isSubmitting)
	}

	function setStatus(message: string, isError = false) {
		statusElement.textContent = message
		statusElement.hidden = false
		statusElement.classList.toggle('do-not-sell__status--error', isError)
		statusElement.setAttribute('role', isError ? 'alert' : 'status')
		if (isError) statusElement.focus()
	}

	function showSuccess() {
		form.reset()
		form.hidden = true
		if (pageHeading) pageHeading.hidden = true
		if (pageDescription) pageDescription.hidden = true
		successPanelElement.hidden = false
		successHeading?.focus()
	}

	async function submitEvent() {
		if (requestInFlight) return

		const data = new FormData(form)
		const honeypot = String(data.get('company') || '').trim()

		if (honeypot) {
			showSuccess()
			return
		}

		if (!window.SegmentUser) {
			setStatus(form.dataset.errorMessage || 'We could not submit your request. Please try again.', true)
			return
		}

		setSubmitting(true)
		statusElement.hidden = true

		try {
			const shopifyConsent = await persistShopifySaleOfDataOptOut()
			const submittedEmail = String(data.get('email') || '').trim().toLowerCase()
			window.SegmentUser.setEmail(submittedEmail)
			const identifyTraits: DoNotSellTraits = {
				first_name: String(data.get('first_name') || '').trim(),
				last_name: String(data.get('last_name') || '').trim(),
				country: String(data.get('country') || '').trim(),
				state: String(data.get('state') || '').trim(),
			}
			const event: DoNotSellEvent = {
				eventName: 'Form Submitted',
				form_name: 'Do Not Sell My Info',
				form_url: 'https://pvolve.com/pages/do-not-sell-my-info',
				__consentCategories: {
					functional: consentAllowed(shopifyConsent.preferences),
					performance: consentAllowed(shopifyConsent.analytics),
					marketing: consentAllowed(shopifyConsent.marketing),
					dataSharing: false,
				},
			}
			const segmentDelivered = await window.SegmentUser.sendAcknowledgedSegmentEvent(identifyTraits, event)
			if (!segmentDelivered) throw new Error('Segment did not confirm the privacy request event.')
			showSuccess()
		} catch (error) {
			console.error('Unable to send do-not-sell Segment event', error)
			setStatus(form.dataset.errorMessage || 'We could not submit your request. Please try again.', true)
		} finally {
			setSubmitting(false)
		}
	}

	form.addEventListener('submit', (event) => {
		event.preventDefault()
		if (requestInFlight || !validateForm()) return

		window.clearTimeout(submitTimer)
		submitTimer = window.setTimeout(submitEvent, SUBMIT_DEBOUNCE_MS)
	})
}

document.querySelectorAll<HTMLFormElement>('[data-do-not-sell-form]').forEach(initDoNotSellForm)
